import { getApiUrl } from '../utils';

export class BaseService {
  protected apiUrl = getApiUrl();

  private static fetchWrapped = false;

  private static unauthorizedError(): any {
    const err: any = new Error('Unauthorized');
    err.status = 401;
    return err;
  }

  private static onUnauthorized() {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('businessUnits');
    } catch {
      // no-op
    }

    // Force navigation without adding a new history entry
    if (typeof window !== 'undefined') {
      window.location.replace('/login');
    }
  }

  // Pre-authentication endpoints: a 401 here means "invalid credential/token", not "your
  // session expired" — must NOT trigger the global logout-and-redirect-to-login behavior,
  // otherwise the caller's own error handling (e.g. AcceptInvitation's invalid-token state)
  // never runs and the user is silently bounced to /login.
  private static isPublicAuthRequest(url: string): boolean {
    return url.includes('/auth/');
  }

  public installGlobalFetchInterceptor() {
    if (typeof window === 'undefined') return;

    if (BaseService.fetchWrapped) return;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const res = await originalFetch(input, init);
      const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;

      if (res.status === 401 && !BaseService.isPublicAuthRequest(url)) {
        BaseService.onUnauthorized();
        throw BaseService.unauthorizedError();
      }
      return res;
    };

    BaseService.fetchWrapped = true;
  }

  protected BuildRequestHeaders = (authorized: boolean, contentType?: string, body?: any) => {
    const headers: any = {};

    try {
      const token = localStorage.getItem('token');
      if (authorized && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch {
      // no-op
    }

    // Don't force Content-Type if sending FormData
    if (contentType && !(body instanceof FormData)) {
      headers['Content-Type'] = contentType;
    }

    return headers;
  };

  protected BuildQueryString = (params: any) => {
    const query = [];
    for (const param in params) {
      if (params[param]) {
        query.push(encodeURIComponent(param) + '=' + encodeURIComponent(params[param]));
      }
    }
    return query.join('&');
  };

  protected async FromResult<T>(result: Response): Promise<T> {
    if (result.status === 401 && !BaseService.isPublicAuthRequest(result.url)) {
      BaseService.onUnauthorized();
      throw BaseService.unauthorizedError();
    }

    const json = await result.json();
    if (result.ok) {
      const result = json as T;
      return result;
    } else {
      if (json.message) throw new Error(json.message);
      else {
        if (json.errors) throw json.errors;
        else throw json;
      }
    }
  }

  protected async Fetch(
    path: string,
    method: string,
    body: any = null,
    authorized: boolean = true,
    contentType: string = 'application/json'
  ): Promise<Response> {
    const res = await fetch(`${this.apiUrl}${path}`, {
      method,
      body,
      headers: this.BuildRequestHeaders(authorized, contentType, body)
    });

    if (res.status === 401 && !BaseService.isPublicAuthRequest(path)) {
      BaseService.onUnauthorized();
      throw BaseService.unauthorizedError();
    }

    return res;
  }

  protected async ProdFetch(
    path: string,
    method: string,
    body: any = null,
    authorized: boolean = true,
    contentType: string = 'application/json'
  ): Promise<Response> {
    const res = await fetch(`https://healthsystemapi.azurewebsites.net${path}`, {
      method,
      body,
      headers: this.BuildRequestHeaders(authorized, contentType, body)
    });

    if (res.status === 401 && !BaseService.isPublicAuthRequest(path)) {
      BaseService.onUnauthorized();
      throw BaseService.unauthorizedError();
    }

    return res;
  }
}

export default BaseService;
